// frontend/apps/widgets/src/widgets/TableReservation/sections/DateStep.js
import React, {useState, useEffect, useMemo, useCallback, useRef} from 'react';
import PropTypes from 'prop-types';
import {format, addMonths, subMonths, isValid, isAfter, startOfMonth} from 'date-fns';
import {fr} from 'date-fns/locale';

// @mui
import {styled, alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';

// icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoIcon from '@mui/icons-material/Info';

// components
import {AvailabilityLegend} from '../components/AvailabilityLegend';
import {ReservationCalendar} from '../components/ReservationCalendar';

// API
import {useExternalAvailableDates} from 'packages/common/hooks/api/widgets/table-reservations/Reservations';

// constants
import {AVAILABILITY_STATUS, ERROR_MESSAGES} from '../constants/reservation';
import {Stack} from '@mui/material';

// ----------------------------------------------------------------------

const CalendarWrapper = styled(Box)(({theme}) => ({
  'position': 'relative',
  'borderRadius': theme.shape.borderRadius,
  '& .MuiTypography-root': {
    fontSize: '0.85rem',
  },
}));

const CalendarHeader = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
}));

const MonthNavButton = styled(IconButton)(({theme}) => ({
  'padding': theme.spacing(0.5),
  '&:not(.Mui-disabled)': {
    color: theme.palette.primary.main,
  },
  '&:hover:not(.Mui-disabled)': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
}));

const SmallLegendButton = styled(Button)(({theme}) => ({
  fontSize: '0.7rem',
  textTransform: 'none',
  padding: theme.spacing(0.25, 0.75),
  marginLeft: theme.spacing(0.5),
  minWidth: 0,
}));

const CalendarSkeletonLoader = styled(Box)(({theme}) => ({
  width: '100%',
  padding: theme.spacing(1),
}));

const CalendarContainer = styled(Box)(({theme}) => ({
  minHeight: 320,
  position: 'relative',
}));

// ----------------------------------------------------------------------

export function DateStep({
  formData,
  updateFormData,
  config,
  companyId,
  apiKey,
  setValidation,
  setErrors,
  errors,
  setStepReady,
}) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availabilityMap, setAvailabilityMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadedMonths, setLoadedMonths] = useState({});
  const [showLegend, setShowLegend] = useState(false);
  const [fetchAttempts, setFetchAttempts] = useState({}); // ✅ AJOUTÉ

  const legendRef = useRef(null);
  const fetchingRef = useRef(false); // ✅ AJOUTÉ

  // Api hooks
  const {getAvailableDates, loading: loadingDates} = useExternalAvailableDates();

  // Fonction de validation pour cette étape
  const validateStep = useCallback(() => {
    if (!formData.date || !isValid(formData.date)) {
      setErrors({date: ERROR_MESSAGES.DATE_REQUIRED});
      return false;
    }

    const dateKey = format(formData.date, 'yyyy-MM-dd');
    if (!availabilityMap[dateKey]) {
      setErrors({date: 'Cette date n\'est pas disponible pour une réservation'});
      return false;
    }

    return true;
  }, [formData.date, availabilityMap, setErrors]);

  // Enregistrer la fonction de validation auprès du parent
  useEffect(() => {
    setValidation(validateStep);
  }, [setValidation, validateStep]);

  // Effect to control next button state based on date selection
  useEffect(() => {
    if (setStepReady) {
      const isDateSelected = formData.date && isValid(formData.date);
      let isDateAvailable = false;
      if (isDateSelected) {
        const dateKey = format(formData.date, 'yyyy-MM-dd');
        isDateAvailable = !!availabilityMap[dateKey];
      }
      setStepReady(isDateSelected && isDateAvailable);
    }
  }, [formData.date, availabilityMap, setStepReady]);

  // Fetch available dates when parameters change
  useEffect(() => {
    if (!companyId || !formData.service || !formData.partySize) {
      return;
    }

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    const monthKey = `${year}-${month}-${formData.service}-${formData.partySize}`; // ✅ DÉFINI ICI

    // Check if we already loaded this month
    if (loadedMonths[monthKey]) {
      return;
    }

    // Check attempts
    if (fetchAttempts[monthKey] && fetchAttempts[monthKey] >= 2) {
      setErrors({api: 'Impossible de charger les dates. Veuillez réessayer plus tard.'});
      return;
    }

    // If already fetching, don't start another request
    if (fetchingRef.current) {
      return;
    }

    fetchingRef.current = true;
    setIsLoading(true);

    // Clear errors
    if (errors.date || errors.api) {
      setErrors({});
    }

    // Increment attempts
    setFetchAttempts((prev) => ({
      ...prev,
      [monthKey]: (prev[monthKey] || 0) + 1,
    }));

    // Fetch function
    const fetchAvailableDates = async () => {
      try {
        const params = {
          service: formData.service,
          partySize: formData.partySize,
          year,
          month,
        };

        const dates = await getAvailableDates(companyId, params, apiKey);

        // Process dates into availability map
        const newAvailabilityMap = {};

        if (Array.isArray(dates)) {
          dates.forEach((dateInfo) => {
            if (dateInfo.__stats__) return;
            const dateKey = dateInfo.date;
            newAvailabilityMap[dateKey] = {
              status: AVAILABILITY_STATUS.AVAILABLE,
            };
          });
        }

        // Update state
        setAvailabilityMap((prev) => ({...prev, ...newAvailabilityMap}));
        setLoadedMonths((prev) => ({...prev, [monthKey]: true}));
      } catch (error) {
        setErrors({api: ERROR_MESSAGES.GENERAL_ERROR});
      } finally {
        setIsLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchAvailableDates();
  }, [companyId, apiKey, formData.service, formData.partySize, currentMonth, getAvailableDates, loadedMonths, fetchAttempts, setErrors, errors.date, errors.api]);

  // Handle month navigation
  const handlePrevMonth = () => {
    const now = new Date();
    const firstDayOfCurrentMonth = startOfMonth(now);
    const prevMonth = subMonths(currentMonth, 1);
    const firstDayOfPrevMonth = startOfMonth(prevMonth);

    if (!isAfter(firstDayOfCurrentMonth, firstDayOfPrevMonth)) {
      setCurrentMonth(prevMonth);
    }
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  // Handle date selection
  const handleDateClick = (date) => {
    updateFormData('date', date);
    if (errors.date) {
      setErrors({});
    }
  };

  // Toggle legend visibility
  const toggleLegend = () => {
    setShowLegend((prev) => {
      const newState = !prev;
      if (newState) {
        setTimeout(() => {
          if (legendRef.current) {
            legendRef.current.scrollIntoView({behavior: 'smooth', block: 'nearest'});
          }
        }, 0);
      }
      return newState;
    });
  };

  // Get booking constraints from config
  const bookingConstraints = useMemo(() => {
    if (!config || !config.bookingPolicy) {
      return {
        advanceBookingDays: 60,
        minimumNoticeHours: 2,
      };
    }

    return {
      advanceBookingDays: config.bookingPolicy.advanceBookingDays || 60,
      minimumNoticeHours: config.bookingPolicy.minimumNoticeHours !== undefined ?
        config.bookingPolicy.minimumNoticeHours :
        2,
    };
  }, [config]);

  // Format current month name
  const currentMonthDisplay = useMemo(() => {
    const monthName = format(currentMonth, 'MMMM', {locale: fr});
    const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
    return `${capitalizedMonth} ${currentMonth.getFullYear()}`;
  }, [currentMonth]);

  // Skeleton loader
  const renderCalendarSkeleton = () => (
    <CalendarSkeletonLoader>
      <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 1}}>
        {[...Array(7)].map((_, index) => (
          <Skeleton
            key={`weekday-${index}`}
            variant="rectangular"
            width={30}
            height={16}
            sx={{borderRadius: 0.5}}
          />
        ))}
      </Box>

      {[...Array(4)].map((_, weekIndex) => (
        <Box
          key={`week-${weekIndex}`}
          sx={{display: 'flex', justifyContent: 'space-between', mb: 1}}
        >
          {[...Array(7)].map((_, dayIndex) => (
            <Skeleton
              key={`day-${weekIndex}-${dayIndex}`}
              variant="circular"
              width={32}
              height={32}
            />
          ))}
        </Box>
      ))}
    </CalendarSkeletonLoader>
  );

  return (
    <Box sx={{p: 0}}>
      <CalendarWrapper sx={{p: 0}}>
        {errors.date && (
          <Alert severity="error" sx={{mb: 1, fontSize: '0.8rem'}}>
            {errors.date}
          </Alert>
        )}

        {errors.api && (
          <Alert severity="error" sx={{mb: 1, fontSize: '0.8rem'}}>
            {errors.api}
          </Alert>
        )}

        <CalendarHeader>
          <Typography variant="subtitle2" sx={{fontSize: '0.85rem', fontWeight: 600}}>
            {currentMonthDisplay}
          </Typography>
          <Box sx={{display: 'flex', alignItems: 'center'}}>
            <MonthNavButton
              size="small"
              onClick={handlePrevMonth}
              disabled={isAfter(startOfMonth(new Date()), startOfMonth(subMonths(currentMonth, 1)))}
            >
              <ArrowBackIcon fontSize="small" />
            </MonthNavButton>
            <MonthNavButton
              size="small"
              onClick={handleNextMonth}
            >
              <ArrowForwardIcon fontSize="small" />
            </MonthNavButton>
          </Box>
        </CalendarHeader>

        <CalendarContainer>
          {isLoading || loadingDates ? (
            renderCalendarSkeleton()
          ) : (
            <Box>
              <ReservationCalendar
                currentMonth={currentMonth}
                availabilityMap={availabilityMap}
                selectedDate={formData.date}
                onDateSelect={handleDateClick}
                bookingConstraints={bookingConstraints}
                exceptions={config?.exceptions || []}
                selectedService={formData.service}
                serviceSchedule={config?.serviceSchedule || {}}
                compact={true}
              />
            </Box>
          )}
        </CalendarContainer>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mt: 0.5}}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              fontSize: '0.7rem',
              textAlign: 'center',
              fontStyle: 'italic',
            }}
          >
            Réservation possible jusqu&apos;à {bookingConstraints.advanceBookingDays} jours
          </Typography>
          <SmallLegendButton
            variant="text"
            color="primary"
            startIcon={<InfoIcon sx={{fontSize: '0.75rem'}} />}
            onClick={toggleLegend}
            size="small"
          >
            Légende
          </SmallLegendButton>
        </Stack>

        {showLegend && (
          <Fade in timeout={300}>
            <Box ref={legendRef} sx={{mt: 0.5}}>
              <AvailabilityLegend />
            </Box>
          </Fade>
        )}
      </CalendarWrapper>
    </Box>
  );
}

DateStep.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  config: PropTypes.object,
  companyId: PropTypes.string,
  apiKey: PropTypes.string,
  setValidation: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setStepReady: PropTypes.func,
};
